import React, { useState, useMemo } from 'react';
import { Table, Form, Button, Card, Row, Col, Dropdown } from 'react-bootstrap';
import { BsCreditCard, BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';

import './CreditCardManager_v01.css'; // Import custom styles

// DynamoDB-style JSON structure

const creditCardData: { [key: string]: { S: string | number } }[] = [
  { id: { S: '1' }, name: { S: 'Platinum Plus' }, issuer: { S: 'Bank A' }, rewards: { S: '2% cashback' }, annualFee: { S: '0' }, apr: { S: '19.99%' } },
  { id: { S: '2' }, name: { S: 'Travel Rewards' }, issuer: { S: 'Bank B' }, rewards: { S: '3x travel points' }, annualFee: { S: '95' }, apr: { S: '17.99%' } },
  { id: { S: '3' }, name: { S: 'Everyday Cashback' }, issuer: { S: 'Bank C' }, rewards: { S: '1.5% cashback' }, annualFee: { S: '0' }, apr: { S: '16.49%' } },
  { id: { S: '4' }, name: { S: 'Student Card' }, issuer: { S: 'Bank D' }, rewards: { S: '1% cashback on all' }, annualFee: { S: '0' }, apr: { S: '21.99%' } },
  { id: { S: '5' }, name: { S: 'Low APR Card' }, issuer: { S: 'Bank E' }, rewards: { S: 'Low interest' }, annualFee: { S: '25' }, apr: { S: '12.99%' } },
  { id: { S: '6' }, name: { S: 'Business Rewards' }, issuer: { S: 'Bank F' }, rewards: { S: '5x office supplies' }, annualFee: { S: '99' }, apr: { S: '18.49%' } },
  { id: { S: '7' }, name: { S: 'Cashback Preferred' }, issuer: { S: 'Bank G' }, rewards: { S: '6% groceries' }, annualFee: { S: '75' }, apr: { S: '20.99%' } },
  { id: { S: '8' }, name: { S: 'Travel Elite' }, issuer: { S: 'Bank H' }, rewards: { S: '5x miles' }, annualFee: { S: '450' }, apr: { S: '22.99%' } },
  { id: { S: '9' }, name: { S: 'Balance Transfer Plus' }, issuer: { S: 'Bank I' }, rewards: { S: '0% intro APR 18 months' }, annualFee: { S: '0' }, apr: { S: '24.99%' } },
  { id: { S: '10' }, name: { S: 'Rewards Unlimited' }, issuer: { S: 'Bank J' }, rewards: { S: 'Unlimited 2% cashback' }, annualFee: { S: '50' }, apr: { S: '19.49%' } },
  { id: { S: '11' }, name: { S: 'Green Travel Card' }, issuer: { S: 'Bank K' }, rewards: { S: 'Carbon offset miles' }, annualFee: { S: '90' }, apr: { S: '18.99%' } },
  { id: { S: '12' }, name: { S: 'Premium Cashback' }, issuer: { S: 'Bank L' }, rewards: { S: '3% on all purchases' }, annualFee: { S: '100' }, apr: { S: '17.99%' } },
  { id: { S: '13' }, name: { S: 'College Student Card' }, issuer: { S: 'Bank M' }, rewards: { S: 'Cashback on textbooks' }, annualFee: { S: '0' }, apr: { S: '23.99%' } },
  { id: { S: '14' }, name: { S: 'Zero Fee Transfer' }, issuer: { S: 'Bank N' }, rewards: { S: '0% intro APR 21 months' }, annualFee: { S: '0' }, apr: { S: '20.99%' } },
  { id: { S: '15' }, name: { S: 'Business Travel Pro' }, issuer: { S: 'Bank O' }, rewards: { S: 'Travel & dining rewards' }, annualFee: { S: '199' }, apr: { S: '18.25%' } },
  { id: { S: '16' }, name: { S: 'Luxury Card Gold' }, issuer: { S: 'Bank P' }, rewards: { S: 'Lounge access & 24k gold' }, annualFee: { S: '995' }, apr: { S: '19.99%' } },
  { id: { S: '17' }, name: { S: 'Everyday Essentials' }, issuer: { S: 'Bank Q' }, rewards: { S: '2% groceries, gas' }, annualFee: { S: '0' }, apr: { S: '16.99%' } },
  { id: { S: '18' }, name: { S: 'Travel Companion' }, issuer: { S: 'Bank R' }, rewards: { S: 'Points & no foreign fee' }, annualFee: { S: '75' }, apr: { S: '17.99%' } },
  { id: { S: '19' }, name: { S: 'No Annual Fee Card' }, issuer: { S: 'Bank S' }, rewards: { S: 'Simple cashback' }, annualFee: { S: '0' }, apr: { S: '22.49%' } },
  { id: { S: '20' }, name: { S: 'Balance Freedom' }, issuer: { S: 'Bank T' }, rewards: { S: '0% for 15 months' }, annualFee: { S: '0' }, apr: { S: '21.49%' } }
];



const CreditCardManager: React.FC = () => {
  const [view, setView] = useState<'table' | 'card'>('table');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<keyof typeof creditCardData[0]>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [hoveredCol, setHoveredCol] = useState<string | null>(null);

  const parsedData = useMemo(
    () =>
      creditCardData.map((item) =>
        Object.fromEntries(
          Object.entries(item).map(([k, v]) => [k, v.S])
        ) as { [key: string]: string }
      ),
    []
  );

  const filteredSortedData = useMemo(() => {
    return parsedData
      .filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => {
        const aVal = a[sortKey].toLowerCase?.() || a[sortKey];
        const bVal = b[sortKey].toLowerCase?.() || b[sortKey];
        if (sortDirection === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      });
  }, [parsedData, search, sortKey, sortDirection]);

  const handleSort = (key: keyof typeof creditCardData[0]) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (key: string) => {
    return sortKey === key ? (
      sortDirection === 'asc' ? <BsCaretUpFill className="ms-1" /> : <BsCaretDownFill className="ms-1" />
    ) : <BsCaretUpFill className="invisible ms-1" />;
  };

  const columnKeys = ['name', 'issuer', 'rewards', 'annualFee', 'apr'];

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Form.Control
          type="text"
          placeholder="Search credit cards..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: '300px' }}
        />

        <div className="d-flex align-items-center gap-2">
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary">Sort by</Dropdown.Toggle>
            <Dropdown.Menu>
              {/* {Object.keys(creditCardData[0]).map((key) => ( */}
              {creditCardData.length > 0 &&
                Object.keys(creditCardData[0]).map((key) => (
                <Dropdown.Item key={key} onClick={() => handleSort(key as any)}>
                  {key}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Button variant={view === 'table' ? 'primary' : 'outline-primary'} onClick={() => setView('table')}>
            Table View
          </Button>
          <Button variant={view === 'card' ? 'primary' : 'outline-primary'} onClick={() => setView('card')}>
            Card View
          </Button>
        </div>
      </div>

      {view === 'table' ? (
        <Table striped bordered hover className="credit-card-table">
          <thead>
            <tr>
              {columnKeys.map((key) => (
                <th
                  key={key}
                  onClick={() => handleSort(key as keyof typeof creditCardData[0])}
                  className={`sortable-header ${hoveredCol === key ? 'header-hover' : ''}`}
                  onMouseEnter={() => setHoveredCol(key)}
                  onMouseLeave={() => setHoveredCol(null)}
                  title={`Sort by ${key} (${sortDirection})`}
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)} {renderSortIcon(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSortedData.map((card, rowIndex) => (
              <tr key={card.id} className="hover-highlight">
                {columnKeys.map((key) => (
                  <td
                    key={key}
                    className={hoveredCol === key ? 'col-hover' : ''}
                  >
                    {key === 'annualFee' ? `$${card[key]}` : card[key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Row>
          {filteredSortedData.map((card) => (
            <Col sm={12} md={6} lg={4} key={card.id} className="mb-4">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <strong>{card.name}</strong>
                  <BsCreditCard />
                </Card.Header>
                <Card.Body>
                  <Card.Text><strong>Issuer:</strong> {card.issuer}</Card.Text>
                  <Card.Text><strong>Rewards:</strong> {card.rewards}</Card.Text>
                  <Card.Text><strong>Annual Fee:</strong> ${card.annualFee}</Card.Text>
                  <Card.Text><strong>APR:</strong> {card.apr}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default CreditCardManager;